import React, { useRef, useEffect, useState } from 'react';
import ReadableText from '../ReadableText';
import ImageLightbox from '../ImageLightbox';
import './ChatArea.css';

const ChatArea = ({
  activeChat,
  messages,
  newMessage,
  onNewMessageChange,
  onSendMessage,
  onNewChat,
  onBack,
  loading,
  t,
  inputRef
}) => {
  const messagesEndRef = useRef(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Orchestration: Once activeChat is non-null and we have a pendingMessage, dispatch it
  useEffect(() => {
    if (activeChat && pendingMessage) {
      if (onSendMessage) {
        // e is null/undefined here since we are dispatching programmatically,
        // so we pass a dummy object with preventDefault for safety
        onSendMessage({ preventDefault: () => {} }, pendingMessage);
      }
      setPendingMessage(null);
    }
  }, [activeChat, pendingMessage, onSendMessage]);

  const handleImageLoad = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInitialConnect = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || loading) return;

    // Stash the user's message so it can be dispatched after chat state is established
    setPendingMessage(newMessage);

    // Call onNewChat to establish the chat state (so activeChat becomes non-null)
    if (onNewChat) {
      onNewChat();
    }
  };

  if (!activeChat) {
    return (
      <div className="chat-area empty cyberpunk-mode">
        <div className="oracle-icon-glitch">Q</div>
        <h2 className="glitch-text" data-text={t('qoph.startTitle')}>{t('qoph.startTitle')}</h2>
        <p className="empty-subtitle">{t('qoph.startBody')}</p>
        <form className="initial-input-area cyber-form" onSubmit={handleInitialConnect}>
          <div className="cyber-input-wrapper">
            <textarea
              ref={inputRef}
              placeholder={t('qoph.startPlaceholder')}
              value={newMessage}
              onChange={(e) => onNewMessageChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleInitialConnect(e);
                }
              }}
              disabled={loading}
              className="cyber-textarea"
            />
            <div className="cyber-input-scanline"></div>
          </div>
          <button type="submit" disabled={loading || !newMessage.trim()} className="cyber-button primary">
            <span className="btn-glitch-content">{loading ? t('qoph.connecting') : t('common.connect')}</span>
            <span className="btn-tag">SYS-LINK</span>
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="chat-area cyberpunk-mode">
      <div className="chat-header-cyber">
        <button className="back-btn-cyber mobile-only" onClick={onBack}>
          <span className="arrow">«</span> {t('common.back')}
        </button>
        <div className={`chat-avatar-cyber ${activeChat.type}-avatar`}>
          {activeChat.type === 'qoph' ? 'Q' : '◎'}
        </div>
        <div className="chat-title-info">
          <h3 className="cyber-title">{activeChat.title}</h3>
          <span className="manifesto-hint-cyber">{activeChat.manifesto}</span>
        </div>
        <div className="header-status-led"></div>
      </div>

      <div className="chat-messages-cyber">
        {messages.map((m, idx) => (
          <div key={m.id || idx} className={`chat-bubble-cyber ${m.role}`} data-agent-role={m.role === 'qoph' ? 'qoph' : undefined}>
            <div className="bubble-content-cyber">
              {m.role === 'qoph' ? (
                <ReadableText text={m.content} label={t('qoph.readResponse')}>
                  {m.content}
                </ReadableText>
              ) : (
                m.content
              )}
            </div>
            {m.imageUrl && (
              <div className="bubble-image-cyber" onClick={() => setLightboxImage(m.imageUrl)}>
                <img
                  src={m.imageUrl}
                  alt="Revelation"
                  className="qoph-revelation-img-cyber"
                  onLoad={handleImageLoad}
                />
              </div>
            )}
            <div className="bubble-time-cyber">
              {m.createdAt ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
              <span className="msg-id"> [#{idx.toString(16).padStart(3, '0')}]</span>
            </div>
          </div>
        ))}
        {loading && (
          <div className="chat-bubble-cyber qoph thinking">
            <div className="bubble-content-cyber">
              <span className="loading-dots">{t('qoph.thinking')}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-area-cyber" onSubmit={onSendMessage}>
        <div className="cyber-input-container">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={t('qoph.promptPlaceholder')}
            value={newMessage}
            onChange={(e) => onNewMessageChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSendMessage(e);
              }
            }}
            disabled={loading}
            className="cyber-input-field"
          />
        </div>
        <button type="submit" disabled={loading || !newMessage.trim()} className="cyber-send-btn">
          <svg viewBox="0 0 24 24" className="send-icon">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
          </svg>
        </button>
      </form>
      <div className="chat-safe-area-bottom"></div>

      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
};

export default ChatArea;
